'use client';

import { useEffect, useState } from 'react';
import { AlertTriangle, BatteryLow, Loader2, MonitorOff } from 'lucide-react';
import { cn } from '@/lib/utils';
import { MANUAL_LIMITS, type PointerMode } from '@/lib/manual-vector';
import {
  MANUAL_LAYOUT_DESCRIPTION,
  MANUAL_LAYOUT_LABEL,
  MANUAL_STAGE_LAYOUTS,
  type ManualStageLayout,
} from '@/lib/manual-layout';
import {
  MANUAL_RING_DESCRIPTION,
  type ManualRingVisibility,
} from '@/lib/manual-rings';
import {
  MANUAL_HANDEDNESS,
  MANUAL_HANDEDNESS_DESCRIPTION,
  MANUAL_HANDEDNESS_LABEL,
  MANUAL_TOUCH_NOTICE,
  manualBatteryWarning,
  type ManualHandedness,
} from '@/lib/manual-touch';
import {
  MANUAL_GROUND_VERBS_SUSPENDED,
  MANUAL_RC_SILENT_MS,
  MANUAL_SESSION_ENDED_REASONS,
  type ManualControlController,
} from '@/hooks/useManualControl';

/**
 * The manual-session strip: the stage layout switcher and RELEASE, and nothing
 * else while the session is healthy.
 *
 * A sibling of the command bar rather than part of it, for the same reason
 * `GotoAltitudeBar` is: the bar is a fixed 60 px toolbar whose key positions must
 * not move, because muscle memory is the point of a command surface. This strip
 * appears only while a manual session exists and disappears with it.
 *
 * It used to also narrate the healthy session — who holds it and since when,
 * the RC lockout, the dead-man countdown, the measured RTT — and field testing
 * (2026-08-16) found that wall of words was the loudest thing on the screen
 * during the one activity where the operator's eyes belong on the stage. So the
 * steady state is now quiet, and words appear only when something is WRONG:
 * OVERRIDE/FAULT, the RC going silent, a pilot override, a refusal or clamp
 * reason, a refused wake lock, a flat tablet. State transitions are still
 * spoken in full by `ManualStatusAnnouncer`, and the border colour still
 * carries the session state at a glance.
 */

const TICK_MS = 250;

export interface ManualFlightBannerProps {
  manual: ManualControlController;
  callsign: string;
  /** Current stage layout. Absent `onLayoutChange` → no switcher at all. */
  layout?: ManualStageLayout;
  /**
   * Change the stage layout. The caller is responsible for cancelling any drag
   * to a hover BEFORE the change lands — `useManualStageLayout` does it, and it
   * is not repeated here, because sending two stops for one action would put a
   * second frame on the control plane for no benefit.
   */
  onLayoutChange?: (layout: ManualStageLayout) => void;
  /** False when the choice could not be written to storage. Said, not hidden. */
  layoutPersisted?: boolean;
  /** Measured pointer. Touch gets the handedness switch and the tooltip text inline. */
  pointer?: PointerMode;
  /** Which bottom corner the control takes. Absent `onHandedChange` → no switch. */
  handed?: ManualHandedness;
  onHandedChange?: (handed: ManualHandedness) => void;
  /** False when the handedness choice could not be persisted. */
  handedPersisted?: boolean;
  /**
   * The screen wake lock was refused or is unavailable. Persistent note, not a
   * toast: the screen can sleep at any point in the session, not just now.
   */
  wakeLockRefused?: boolean;
  /**
   * Whether the calibration rings are painted at rest. Absent
   * `onRingVisibilityChange` → no toggle at all.
   */
  ringVisibility?: ManualRingVisibility;
  onRingVisibilityChange?: (visibility: ManualRingVisibility) => void;
  /** False when the ring choice could not be persisted. */
  ringsPersisted?: boolean;
  /** Device battery 0–1 while below the warn threshold; null when fine or unknown. */
  batteryLevel?: number | null;
  /** Injected clock, for tests. */
  now?: number;
}

export function ManualFlightBanner({
  manual,
  callsign,
  layout,
  onLayoutChange,
  layoutPersisted = true,
  pointer = 'desktop',
  handed,
  onHandedChange,
  handedPersisted = true,
  wakeLockRefused = false,
  ringVisibility,
  onRingVisibilityChange,
  ringsPersisted = true,
  batteryLevel = null,
  now,
}: ManualFlightBannerProps) {
  // Faster than the COP's usual 1 Hz on purpose: the RC-silent counter below is
  // the thing an operator watches to decide whether the link is gone, and a
  // clock that steps in whole seconds reads as a stopped clock exactly then.
  const [tick, setTick] = useState(() => Date.now());
  useEffect(() => {
    if (now !== undefined) return;
    const id = window.setInterval(() => setTick(Date.now()), TICK_MS);
    return () => window.clearInterval(id);
  }, [now]);
  const clockMs = now ?? tick;

  const held = manual.holdsAuthority;
  const alarm = manual.state === 'OVERRIDE' || manual.state === 'FAULT';

  /**
   * How long since the RC last said anything, while we still claim its aircraft.
   *
   * The RC publishes at 1 Hz whenever it holds authority, so silence is a
   * measurement, not an absence of one. Only counted once a first frame has
   * arrived: between the grant and that frame there is nothing to be silent
   * about.
   */
  const silentMs =
    held && manual.lastStateAtMs !== null ? Math.max(0, clockMs - manual.lastStateAtMs) : null;
  const rcSilent = silentMs !== null && silentMs > MANUAL_RC_SILENT_MS;

  return (
    <div
      role="group"
      aria-label={`Manual flight control for ${callsign}`}
      className={cn(
        'rounded-lg border bg-card px-3 py-2',
        alarm
          ? 'border-status-red/50'
          : rcSilent
            ? 'border-status-amber/60'
            : held
              ? 'border-status-green/50'
              : 'border-status-amber/40',
      )}
    >
      {/* Centred, because the strip sits above the stage: the switcher and
          RELEASE line up over the map rather than hanging off its right edge,
          and a warning joins the same centred cluster when there is one. */}
      <div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-1.5">
        {/* Silent while the session is healthy. A word appears here only when
            something is wrong — OVERRIDE and FAULT as words, never a hue alone,
            and the RC-silent counter, which is the difference between a link
            problem the operator can see and one they discover afterwards. The
            announcement is NOT here: the live region is `ManualStatusAnnouncer`,
            mounted by the COP whether or not there is a session, because a
            region that appears with its content is one whose first change is
            never heard.

            OVERRIDE and FAULT get a BAR, not a word in the row. They are the
            most consequential state changes this feature has — a human took the
            sticks, or the RC faulted — and they used to render as 10 px of mono
            plus a border-colour change, the smallest text on a screen otherwise
            full of larger type. `docs/operational-standards.md` asks for a
            full-width bar for a critical alert, so the alarm claims its own row
            (`w-full` inside the wrapping flex row), paints its own ground and
            comes up to the size of the words around it. On their own the
            RC-silent counter and the grant/release spinner stay the quiet inline
            note they were — promoting those too would spend the loudest
            treatment this strip has on states that are not the alarm — and when
            an alarm is up as well the counter rides inside the bar, because the
            two facts belong to one event.

            NOT accompanied by a tone. An audible alert is a product decision,
            not a styling one, and nobody has asked for one. */}
        {(manual.busy || alarm || rcSilent) && (
          <span
            data-testid="manual-state-word"
            data-alarm={alarm ? '' : undefined}
            className={cn(
              'flex items-center gap-1.5 uppercase',
              alarm
                ? 'w-full justify-center rounded bg-status-red/20 px-3 py-1.5 text-[13px] font-bold tracking-[0.18em] text-status-red-fg ring-1 ring-status-red/60'
                : 'text-[10px] tracking-[0.12em]',
              !alarm && (rcSilent ? 'text-status-amber-fg' : 'text-muted-foreground'),
            )}
          >
            {manual.busy && <Loader2 className="size-3 animate-spin" strokeWidth={2} />}
            {(alarm || rcSilent) && (
              <AlertTriangle
                className={cn('shrink-0', alarm ? 'size-4' : 'size-3')}
                strokeWidth={alarm ? 2.5 : 1.5}
              />
            )}
            {alarm && `Manual ${manual.label}`}
            {alarm && rcSilent && ' · '}
            {rcSilent && `RC silent ${Math.round((silentMs ?? 0) / 1000)}s`}
            {/* Busy with nothing wrong is the grant/release round trip — the
                spinner is the whole message, and the announcer says the word. */}
            {manual.busy && !alarm && !rcSilent && (
              <span className="sr-only">Manual {manual.label}</span>
            )}
          </span>
        )}

        <div className="flex shrink-0 items-center gap-1.5">
          {/* Touch only: on a mouse the cursor reaches everywhere equally, so a
              handedness choice would be a control with nothing to change. */}
          {pointer === 'touch' && handed && onHandedChange && (
            <HandedSwitch handed={handed} onChange={onHandedChange} persisted={handedPersisted} />
          )}
          {ringVisibility && onRingVisibilityChange && (
            <RingsToggle
              visibility={ringVisibility}
              onChange={onRingVisibilityChange}
              persisted={ringsPersisted}
            />
          )}
          {layout && onLayoutChange && (
            <LayoutSwitch layout={layout} onChange={onLayoutChange} persisted={layoutPersisted} />
          )}
          <button
            type="button"
            onClick={() => manual.release('operator')}
            disabled={manual.busy && manual.state === 'RELEASING'}
            // 44 px, like every other control this feature ships. It was 36,
            // which is the one key on this strip that hands the aircraft back —
            // the last control that should be harder to hit than the rest.
            className="min-h-11 rounded border border-status-red/40 bg-status-red/10 px-3 py-1 text-[11px] uppercase tracking-wider text-status-red-fg hover:bg-status-red/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-40"
          >
            Release
            <kbd className="ml-1.5 text-[9px] tracking-wider text-muted-foreground">ESC</kbd>
          </button>
        </div>
      </div>

      {/* The screen may sleep mid-vector, taking STOP with it. Said once and
          left there, because the risk lasts as long as the session does. */}
      {wakeLockRefused && (
        <p
          role="status"
          aria-live="polite"
          data-testid="manual-wake-lock-note"
          className="mt-1 flex items-start gap-1.5 text-[10px] leading-relaxed text-status-amber-fg"
        >
          <MonitorOff className="mt-px size-3 shrink-0" strokeWidth={1.5} />
          <span>
            <span className="font-bold uppercase tracking-[0.12em]">
              {MANUAL_TOUCH_NOTICE.wakeLockRefused}
            </span>{' '}
            — {MANUAL_TOUCH_NOTICE.wakeLockDetail}
          </span>
        </p>
      )}

      {/* Only ever rendered where the Battery Status API answered. Where it does
          not exist there is no warning and no placeholder — a battery reading
          nobody measured is not something to invent. */}
      {batteryLevel !== null && (
        <p
          role="status"
          aria-live="polite"
          data-testid="manual-battery-note"
          className="mt-1 flex items-start gap-1.5 text-[10px] leading-relaxed text-status-amber-fg"
        >
          <BatteryLow className="mt-px size-3 shrink-0" strokeWidth={1.5} />
          <span>{manualBatteryWarning(batteryLevel)}</span>
        </p>
      )}

      {/* Whatever the last thing that happened was — a refusal, a slow-link
          warning, an RC clamp, an override. One line, in words, never dropped
          silently. Not a live region of its own: `ManualStatusAnnouncer` carries
          the same sentence, and it is mounted before there is anything to say. */}
      {(manual.reason || manual.clampDetail) && (
        <p
          data-testid="manual-reason"
          className="mt-1 text-[10px] leading-relaxed text-muted-foreground"
        >
          {manual.reason ?? manual.clampDetail}
        </p>
      )}

      {manual.pilotOverride && (
        <p className="mt-1 flex items-start gap-1.5 text-[10px] leading-relaxed text-status-red-fg">
          <AlertTriangle className="mt-px size-3 shrink-0" strokeWidth={1.5} />
          <span>Pilot has the sticks at the RC. RTH, LAND and ABORT still work from here.</span>
        </p>
      )}

      {/* Only rendered while the keys are actually taken, so it is never advice
          about a state the operator is not in. */}
      {held && <span className="sr-only">{MANUAL_GROUND_VERBS_SUSPENDED}</span>}
    </div>
  );
}

/**
 * The manual state, announced — and the only live region this feature has.
 *
 * Mounted by the COP UNCONDITIONALLY, empty until there is something to say.
 * That is the whole point: a live region has to be in the DOM before its content
 * changes, or the first change is the one nobody hears. The banner appears at
 * the same instant REQUESTING does, so a region inside it could never announce
 * the transition that created it. `VideoLatencyBadge` makes the same argument
 * about its picture-settled line, and this is the same fix.
 *
 * It carries the reason as well as the state word, because the reason is the
 * half an operator can act on — and because a refusal that only ever existed as
 * a four-second toast is the finding this pass exists to close.
 */
export function ManualStatusAnnouncer({
  manual,
  callsign = null,
}: {
  manual: ManualControlController;
  callsign?: string | null;
}) {
  const quiet = (manual.state === 'IDLE' || manual.state === 'UNAVAILABLE') && !manual.reason;
  const target = callsign ? ` — ${callsign}` : '';
  return (
    <p role="status" aria-live="polite" data-testid="manual-announcer" className="sr-only">
      {quiet ? '' : `Manual ${manual.label}${target}.${manual.reason ? ` ${manual.reason}` : ''}`}
    </p>
  );
}

/**
 * A refusal that outlives the toast that carried it.
 *
 * Every server-side refusal — held by another operator, no command secret, link
 * too slow, the RC's own `denied` detail, the grant timing out — arrives as a
 * sentence and then puts the controller back in IDLE, which is exactly the state
 * in which the banner is not mounted. The refusal was therefore readable for the
 * four seconds a toast lives and nowhere afterwards, with the persistent copy
 * hiding in a `title` attribute that never fires on the touch device this
 * feature targets and is unreachable by keyboard on a disabled button.
 *
 * So this strip stays up until something replaces it: the next request clears
 * the reason, a grant supersedes it, and a different aircraft brings its own.
 */
export function ManualRefusalNote({
  reason,
  callsign,
  operator = null,
}: {
  reason: string;
  callsign: string;
  /**
   * The signed-in operator. When the refusal names THEM, it is almost always
   * their own previous page load still holding the lock — which reads as
   * somebody else having taken the aircraft unless it is named.
   */
  operator?: string | null;
}) {
  const selfHeld =
    operator !== null && operator.length > 0 && reason.includes(operator);
  // Several sentences reach this strip that are not refusals at all: the session
  // ENDED, because the operator picked another aircraft, reloaded the page, or
  // one of the two dead-men fired. Labelling those "refused" under the newly
  // selected callsign would be a claim about an aircraft nobody has been refused.
  const ended = MANUAL_SESSION_ENDED_REASONS.has(reason);
  return (
    <div
      data-testid="manual-refusal-note"
      className={cn(
        'flex items-start gap-2 rounded-lg border bg-card px-3 py-2 text-[10px] leading-relaxed',
        ended ? 'border-subtle text-muted-foreground' : 'border-status-amber/40 text-status-amber-fg',
      )}
    >
      <span aria-hidden className="mt-px">{ended ? '◉' : '⃠'}</span>
      <span>
        <span className="font-bold uppercase tracking-[0.12em]">
          {ended ? 'Manual ended' : `Manual refused · ${callsign}`}
        </span>{' '}
        — {reason}
        {selfHeld && (
          <span className="mt-0.5 block text-muted-foreground">
            That is this account. A session from a previous page load still holds the lock; it
            clears itself within {Math.round(MANUAL_LIMITS.HEARTBEAT_TIMEOUT_MS / 1000)} seconds.
          </span>
        )}
      </span>
    </div>
  );
}

/**
 * Where the operator chooses how much of the screen is map and how much is
 * picture.
 *
 * On the banner rather than on the command bar, deliberately: the bar is a
 * fixed toolbar whose key positions must not move, and this control only exists
 * while a manual session does. It is also where the operator is already
 * looking — the layout question comes up in the middle of flying, not before it.
 *
 * Three buttons rather than a cycling toggle, because a cycle makes the
 * operator press twice and read the result to get somewhere, and each is 44 px
 * so it can be hit without looking (WCAG 2.5.8 wants 24; a control used with an
 * aircraft in the air gets the larger target).
 */
/**
 * Paint the calibration rings at rest, or only while commanding.
 *
 * A display choice and nothing else: the geometry is computed either way and
 * the vector clamps against the same radii, so this key can never change what
 * the aircraft is told. It does NOT hide the yaw band — that band is the only
 * pointer path to yaw, and an invisible control is not a tidier one.
 *
 * One key rather than a two-button group: the states are exclusive and the
 * off state still shows rings during a gesture, which a segmented
 * ALWAYS / NEVER pair would misdescribe.
 */
function RingsToggle({
  visibility,
  onChange,
  persisted,
}: {
  visibility: ManualRingVisibility;
  onChange: (visibility: ManualRingVisibility) => void;
  persisted: boolean;
}) {
  const always = visibility === 'always';
  return (
    <div className="flex items-center gap-1 rounded border border-subtle p-0.5">
      <button
        type="button"
        aria-pressed={always}
        data-testid="manual-rings-toggle"
        onClick={() => onChange(always ? 'active' : 'always')}
        title={MANUAL_RING_DESCRIPTION[visibility]}
        className={cn(
          'min-h-11 min-w-11 rounded px-2 text-[11px] uppercase tracking-[0.1em] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
          always
            ? 'bg-status-blue/20 font-bold text-status-blue-fg ring-1 ring-status-blue/50'
            : 'text-muted-foreground hover:bg-accent hover:text-foreground',
        )}
      >
        RINGS
        {/* The state is a word, not only a hue and a ring — `aria-pressed`
            alone leaves a forced-colors operator reading one unchanging key. */}
        <span className="sr-only">
          {' — '}
          {MANUAL_RING_DESCRIPTION[visibility]}
        </span>
      </button>
      {/* Announced once, in words, for the operator who cannot see the rings
          leave the map. */}
      <span role="status" aria-live="polite" className="sr-only">
        {MANUAL_RING_DESCRIPTION[visibility]}
      </span>
      {!persisted && (
        <span className="sr-only">Ring preference could not be saved for next session.</span>
      )}
    </div>
  );
}

function LayoutSwitch({
  layout,
  onChange,
  persisted,
}: {
  layout: ManualStageLayout;
  onChange: (layout: ManualStageLayout) => void;
  persisted: boolean;
}) {
  return (
    <div
      role="group"
      aria-label="Stage layout"
      data-testid="manual-layout-switch"
      className="flex items-center gap-1 rounded border border-subtle p-0.5"
    >
      {MANUAL_STAGE_LAYOUTS.map((option) => {
        const active = option === layout;
        return (
          <button
            key={option}
            type="button"
            aria-pressed={active}
            data-testid={`manual-layout-${option}`}
            onClick={() => onChange(option)}
            title={MANUAL_LAYOUT_DESCRIPTION[option]}
            className={cn(
              'min-h-11 min-w-11 rounded px-2 text-[11px] uppercase tracking-[0.1em] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
              active
                ? 'bg-status-blue/20 font-bold text-status-blue-fg ring-1 ring-status-blue/50'
                : 'text-muted-foreground hover:bg-accent hover:text-foreground',
            )}
          >
            {MANUAL_LAYOUT_LABEL[option]}
            {/* The selected one is announced as a word, not only as a ring and a
                hue — `aria-pressed` alone leaves a forced-colors operator
                reading three identical keys. */}
            <span className="sr-only">
              {' — '}
              {MANUAL_LAYOUT_DESCRIPTION[option]}
              {active ? ' (current layout)' : ''}
            </span>
          </button>
        );
      })}
      {/* The change itself is announced, once, in words — the operator who
          cannot see the stage re-shape is owed the same information the
          re-shape gives everyone else. */}
      <span role="status" aria-live="polite" className="sr-only">
        Stage layout {MANUAL_LAYOUT_LABEL[layout]} — {MANUAL_LAYOUT_DESCRIPTION[layout]}
      </span>
      {!persisted && (
        <span className="sr-only">
          This layout could not be saved, so it will not be remembered next time.
        </span>
      )}
    </div>
  );
}

/**
 * Which bottom corner the control takes, and which one STOP takes.
 *
 * A tablet is held two-handed, which makes the centre of the screen the least
 * reachable place on it — the opposite of a desktop. So on touch the whole
 * control moves into a bottom corner and STOP into the other, and which is
 * which is the operator's, remembered per operator exactly like the layout is.
 *
 * Two 44 px keys rather than a toggle, for the same reason the layout switcher
 * has three: a toggle makes the operator press and then read the result to find
 * out where they are.
 */
function HandedSwitch({
  handed,
  onChange,
  persisted,
}: {
  handed: ManualHandedness;
  onChange: (handed: ManualHandedness) => void;
  persisted: boolean;
}) {
  return (
    <div
      role="group"
      aria-label="Handedness"
      data-testid="manual-handed-switch"
      className="flex items-center gap-1 rounded border border-subtle p-0.5"
    >
      <span className="px-1 text-[9px] uppercase tracking-[0.1em] text-muted-foreground">
        Handed
      </span>
      {MANUAL_HANDEDNESS.map((option) => {
        const active = option === handed;
        return (
          <button
            key={option}
            type="button"
            aria-pressed={active}
            data-testid={`manual-handed-${option}`}
            onClick={() => onChange(option)}
            className={cn(
              'min-h-11 min-w-11 rounded px-2 text-[11px] uppercase tracking-[0.1em] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
              active
                ? 'bg-status-blue/20 font-bold text-status-blue-fg ring-1 ring-status-blue/50'
                : 'text-muted-foreground hover:bg-accent hover:text-foreground',
            )}
          >
            {option}
            {/* The choice is a word, not only a highlighted letter — the same
                rule the layout switcher follows, and for the same operator. */}
            <span className="sr-only">
              {' — '}
              {MANUAL_HANDEDNESS_LABEL[option]}: {MANUAL_HANDEDNESS_DESCRIPTION[option]}
              {active ? ' (current)' : ''}
            </span>
          </button>
        );
      })}
      <span role="status" aria-live="polite" className="sr-only">
        {MANUAL_HANDEDNESS_LABEL[handed]} handed — {MANUAL_HANDEDNESS_DESCRIPTION[handed]}
      </span>
      {!persisted && (
        <span className="sr-only">
          This handedness could not be saved, so it will not be remembered next time.
        </span>
      )}
    </div>
  );
}
